import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/ui-kit/switch';
import { isWorkingFromHome, sortOfficeDays } from '../../schemas/work-status';
import { Employee } from '../../types/employee.types';
import { employeeName } from '../../schemas/display-name';

const nameOf = (employee: Employee) =>
  employeeName(employee);

/** Keeps a click on the switch from also opening the employee's profile (the row is clickable). */
const stopRowClick = (event: React.SyntheticEvent) => event.stopPropagation();

interface StatusSwitchProps {
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}

const StatusSwitch = ({ label, checked, disabled, onChange }: StatusSwitchProps) => (
  <Switch
    checked={checked}
    disabled={disabled}
    onCheckedChange={onChange}
    onClick={stopRowClick}
    aria-label={label}
  />
);

export interface WorkStatusCellProps {
  employee: Employee;
  /** The switch is inert while a change to this employee is being saved. */
  isSaving: boolean;
  onChange: (employee: Employee, next: boolean) => void;
}

/**
 * Work from home on / off. While it is off, the days the person comes to the office are listed
 * under the switch and open the day picker again.
 */
export const WfhCell = ({
  employee,
  isSaving,
  onChange,
  onEditOfficeDays,
}: WorkStatusCellProps & { onEditOfficeDays: (employee: Employee) => void }) => {
  const { t } = useTranslation();
  const officeDays = sortOfficeDays(employee.WFODays);
  const isWfh = isWorkingFromHome(employee);
  const showOfficeDays = !isWfh && officeDays.length > 0;

  return (
    <div className="flex flex-col items-start gap-1">
      <StatusSwitch
        label={`${t('WFH')} - ${nameOf(employee)}`}
        checked={isWfh}
        disabled={isSaving}
        onChange={(next) => onChange(employee, next)}
      />
      {showOfficeDays && (
        <button
          type="button"
          disabled={isSaving}
          title={t('WORK_FROM_OFFICE')}
          onClick={(event) => {
            stopRowClick(event);
            onEditOfficeDays(employee);
          }}
          className="whitespace-nowrap rounded text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {officeDays.map((day) => t(day.slice(0, 3))).join(', ')}
        </button>
      )}
    </div>
  );
};
